import { useEffect } from 'react';

const getLiveEchartsInstance = chartRef => {
  if (!chartRef?.current?.getEchartsInstance) return null;

  try {
    const instance = chartRef.current.getEchartsInstance();
    if (!instance || instance.isDisposed?.()) return null;
    return instance;
  } catch {
    return null;
  }
};

const hideChartTooltip = chartRef => {
  const instance = getLiveEchartsInstance(chartRef);
  if (!instance) return;

  try {
    instance.dispatchAction({ type: 'hideTip' });
  } catch {
    // Chart instance may already be disposed during unmount/navigation.
  }
};

const bindGlobalOut = (instance, handler) => {
  try {
    instance.on('globalout', handler);
  } catch {
    // ignore
  }
};

const unbindGlobalOut = (instance, handler) => {
  if (!instance || instance.isDisposed?.()) return;

  try {
    instance.off('globalout', handler);
  } catch {
    // ignore
  }
};

export const createShouldSetEchartOption = chartRef => () => {
  if (!chartRef?.current?.getEchartsInstance) return true;

  try {
    const instance = chartRef.current.getEchartsInstance();
    if (!instance) return true;
    return !instance.isDisposed?.();
  } catch {
    return false;
  }
};

export const useTooltipHide = chartRef => {
  useEffect(() => {
    if (!chartRef) return;

    let instance = null;
    let cancelled = false;
    let retryId = null;

    const handleGlobalOut = () => {
      hideChartTooltip(chartRef);
    };

    const detachGlobalOut = () => {
      if (instance) {
        unbindGlobalOut(instance, handleGlobalOut);
        instance = null;
      }
    };

    const attachToLiveInstance = () => {
      const current = getLiveEchartsInstance(chartRef);
      if (!current) return false;

      if (instance !== current) {
        detachGlobalOut();
        instance = current;
        bindGlobalOut(instance, handleGlobalOut);
      }

      return true;
    };

    let attempts = 0;
    const maxAttempts = 20;
    const retryDelayMs = 50;

    const tryAttach = () => {
      if (cancelled) return;

      if (attachToLiveInstance()) return;

      attempts += 1;
      if (attempts < maxAttempts) {
        retryId = setTimeout(tryAttach, retryDelayMs);
      }
    };

    tryAttach();

    const handleScroll = () => {
      hideChartTooltip(chartRef);
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      cancelled = true;
      if (retryId) clearTimeout(retryId);
      window.removeEventListener('scroll', handleScroll, true);
      detachGlobalOut();
    };
  }, [chartRef]);
};
